import React, { useState, useRef } from 'react';
import {
  X,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Info,
  FileUp,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import ClinicService from './Clinic/ClinicService';
import DoctorService from './Doctor/DoctorService';
import { apiFetch } from '../services/api';
import Cookies from 'js-cookie';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001'}/admin`;

const BulkUploadModal = ({
  isOpen,
  onClose,
  type = 'clinic', // 'clinic' | 'doctor'
  existingList = [],
  specialities = [],
  clinics = [],
  onSuccess,
}) => {
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [filterTab, setFilterTab] = useState('all'); // 'all' | 'create' | 'update' | 'error'
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0, currentItem: '' });
  const [results, setResults] = useState(null); // { created: 0, updated: 0, failed: 0, log: [] }
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const isClinic = type === 'clinic';
  const entityTitle = isClinic ? 'Clinic' : 'Doctor';

  // ─────────────────────────────────────────────────────────────
  // 1. TEMPLATE GENERATION & DOWNLOAD
  // ─────────────────────────────────────────────────────────────
  const handleDownloadTemplate = () => {
    let headers = [];
    let sampleData = [];
    let instructions = [];

    if (isClinic) {
      headers = [
        'ID (Leave blank for new, fill to update)',
        'Clinic Name*',
        'Email*',
        'Mobile Number*',
        'Location*',
        'Address*',
        'Registration Number*',
        'License Expiry Date (YYYY-MM-DD)*',
        'Latitude',
        'Longitude',
        'Password (Required for new)',
        'Billing Model (subscription/commission)',
        'Subscription Amount',
        'Subscription Type (monthly/yearly)',
        'Commission Rate',
        'Commission Type (percentage/fixed)',
      ];

      sampleData = [
        [
          '', // New
          'City Care Health Clinic',
          'citycare@example.com',
          '9876543210',
          'Coimbatore',
          '123 Avinashi Road, Coimbatore',
          'REG-CC-2024-01',
          '2026-12-31',
          '11.0168',
          '76.9558',
          'Admin@123',
          'subscription',
          '5000',
          'monthly',
          '',
          '',
        ],
        [
          '16', // Update sample
          'Apollo Family Care',
          'apollofamily@example.com',
          '9876543211',
          'Bangalore',
          '45 Indiranagar 100ft Road, Bangalore',
          'REG-AF-2024-02',
          '2027-06-30',
          '12.9716',
          '77.5946',
          '',
          'commission',
          '',
          '',
          '10',
          'percentage',
        ],
      ];

      instructions = [
        ['INSTRUCTIONS FOR CLINIC BULK UPLOAD / UPDATE'],
        ['1. UPDATE vs NEW:'],
        ['   - If you provide an ID matching an existing clinic, or if Email / Mobile / Reg No matches, the system will UPDATE that clinic.'],
        ['   - If no match is found, the system will CREATE a new clinic.'],
        ['2. Mandatory fields for new clinics are marked with (*).'],
        ['3. Latitude & Longitude (e.g. 11.0168, 76.9558) are used by the patient app for location-based discovery.'],
        ['4. License Expiry Date format must be YYYY-MM-DD (e.g. 2026-12-31).'],
        ['5. Mobile number must be a valid 10-digit number.'],
      ];
    } else {
      headers = [
        'ID (Leave blank for new, fill to update)',
        'Title (Dr./Prof.)',
        'Doctor Name*',
        'Email*',
        'Mobile Number*',
        'Specialty (Name or ID)*',
        'Education (MBBS/MD)*',
        'Experience (Years)',
        'Gender (Male/Female/Other)',
        'Registration Number',
        'Location',
        'Address',
        'Clinic (Name or ID)*',
        'Languages',
        'Commitment Type (normal/committed)',
        'Password (Required for new)',
      ];

      const sampleClinic = clinics[0]?.vendor_name || '16';
      const sampleSpecialty = specialities[0]?.speciality || 'General Medicine';

      sampleData = [
        [
          '', // New
          'Dr.',
          'Anand Sharma',
          'dranand@example.com',
          '9811223344',
          sampleSpecialty,
          'MBBS, MD (General Medicine)',
          '10',
          'Male',
          'TN-MED-12345',
          'Coimbatore',
          '123 Gandhipuram, Coimbatore',
          sampleClinic,
          'English, Tamil, Hindi',
          'committed',
          'Doctor@123',
        ],
        [
          '77', // Update sample
          'Dr.',
          'Priya Sundaram',
          'drpriya@example.com',
          '9822334455',
          'Pediatrics',
          'MBBS, DCH',
          '7',
          'Female',
          'KA-MED-67890',
          'Bangalore',
          '45 Indiranagar, Bangalore',
          sampleClinic,
          'English, Kannada',
          'normal',
          '',
        ],
      ];

      instructions = [
        ['INSTRUCTIONS FOR DOCTOR BULK UPLOAD / UPDATE'],
        ['1. UPDATE vs NEW:'],
        ['   - If you provide an ID matching an existing doctor, or if Email / Mobile matches, the system will UPDATE that doctor.'],
        ['   - If no match is found, the system will CREATE a new doctor.'],
        ['2. Mandatory fields for new doctors are marked with (*): Doctor Name, Email, Mobile Number, Specialty, Education, Clinic.'],
        ['3. Specialty: You can enter either the Specialty Name (e.g. "Cardiology") or Specialty ID.'],
        ['4. Clinic: You can enter either the Clinic Name (e.g. "City Care") or Clinic ID. Doctor inherits GPS location from this Clinic.'],
        ['5. Commitment Type can be "normal" or "committed" (Assured Doctor).'],
      ];
    }

    const wb = XLSX.utils.book_new();

    // Data worksheet
    const wsData = [headers, ...sampleData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-fit column widths
    const colWidths = headers.map((h) => ({ wch: Math.max(h.length + 4, 15) }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, `${entityTitle} Template`);

    // Instructions worksheet
    const wsInst = XLSX.utils.aoa_to_sheet(instructions);
    wsInst['!cols'] = [{ wch: 90 }];
    XLSX.utils.book_append_sheet(wb, wsInst, 'Instructions');

    XLSX.writeFile(wb, `${entityTitle}_Bulk_Upload_Template.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────
  // 2. EXPORT CURRENT DATABASE TO EXCEL
  // ─────────────────────────────────────────────────────────────
  const handleExportExisting = () => {
    if (!existingList || existingList.length === 0) {
      alert(`No existing ${type}s to export.`);
      return;
    }

    let rows = [];
    if (isClinic) {
      rows = existingList.map((c) => ({
        'ID (Leave blank for new, fill to update)': c.id || '',
        'Clinic Name*': c.vendor_name || c.vendorName || '',
        'Email*': c.vendor_email || c.vendorEmail || '',
        'Mobile Number*': c.vendor_phone || c.vendorConNumber || '',
        'Location*': c.vendor_location || '',
        'Address*': c.vendor_address || '',
        'Registration Number*': c.reg_no || '',
        'License Expiry Date (YYYY-MM-DD)*': c.exp_date ? String(c.exp_date).slice(0, 10) : '',
        'Latitude': c.vendor_latlong ? c.vendor_latlong.split(',')[0]?.trim() : '',
        'Longitude': c.vendor_latlong ? c.vendor_latlong.split(',')[1]?.trim() : '',
        'Password (Required for new)': '',
        'Billing Model (subscription/commission)': c.billing_model || 'subscription',
        'Subscription Amount': c.subscription_amount || '',
        'Subscription Type (monthly/yearly)': c.subscription_type || 'monthly',
        'Commission Rate': c.commission_rate || '',
        'Commission Type (percentage/fixed)': c.commission_type || 'percentage',
      }));
    } else {
      rows = existingList.map((d) => ({
        'ID (Leave blank for new, fill to update)': d.id || '',
        'Title (Dr./Prof.)': d.titleType || 'Dr.',
        'Doctor Name*': d.vendorName || d.vendor_name || '',
        'Email*': d.vendorEmail || d.vendor_email || '',
        'Mobile Number*': d.vendorConNumber || d.vendor_phone || '',
        'Specialty (Name or ID)*': d.specialty || '',
        'Education (MBBS/MD)*': d.education || '',
        'Experience (Years)': d.experience || '',
        'Gender (Male/Female/Other)': d.gender || '',
        'Registration Number': d.regNumber || '',
        'Location': d.location || '',
        'Address': d.address || '',
        'Clinic (Name or ID)*': d.clinic || d.clinic_id || '',
        'Languages': d.languages || '',
        'Commitment Type (normal/committed)': d.commitment_type || (d.is_committed ? 'committed' : 'normal'),
        'Password (Required for new)': '',
      }));
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, `Current ${entityTitle}s`);
    XLSX.writeFile(wb, `Current_${entityTitle}s_Export.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────
  // 3. PARSE UPLOADED EXCEL/CSV FILE
  // ─────────────────────────────────────────────────────────────
  const normalizeKey = (k) => String(k || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const parseRowFields = (row) => {
    const rawMap = {};
    for (const [key, value] of Object.entries(row)) {
      rawMap[normalizeKey(key)] = typeof value === 'string' ? value.trim() : value;
    }

    const getVal = (possibleKeys) => {
      for (const pk of possibleKeys) {
        const norm = normalizeKey(pk);
        if (rawMap[norm] !== undefined && rawMap[norm] !== '') {
          return rawMap[norm];
        }
      }
      return '';
    };

    const id = getVal(['id', 'clinicid', 'doctorid', 'vendorid']);
    const email = String(getVal(['email', 'vendoremail', 'clinicemail', 'doctoremail', 'mail'])).trim();
    const phone = String(getVal(['mobilenumber', 'mobile', 'phone', 'vendorphone', 'vendorconnumber', 'contactnumber', 'contact'])).replace(/\D/g, '');
    const name = String(getVal(['clinicname', 'doctorname', 'vendorname', 'name', 'fullname'])).trim();
    const location = String(getVal(['location', 'city', 'area', 'vendorlocation'])).trim();
    const address = String(getVal(['address', 'clinicaddress', 'doctoraddress', 'vendoraddress'])).trim();
    const regNumber = String(getVal(['registrationnumber', 'regnumber', 'regno', 'reg_no', 'medicallicensenumber'])).trim();
    const lat = String(getVal(['latitude', 'lat'])).trim();
    const lng = String(getVal(['longitude', 'long', 'lng'])).trim();
    const rawLatLong = String(getVal(['latlong', 'lat_long', 'latitudeandlongitude', 'coordinates'])).trim();
    const password = String(getVal(['password', 'pwd'])).trim();

    let combinedLatLong = '';
    if (lat && lng) {
      combinedLatLong = `${lat},${lng}`;
    } else if (rawLatLong) {
      combinedLatLong = rawLatLong;
    }

    if (isClinic) {
      const expDate = String(getVal(['licenseexpirydate', 'licenseexpiry', 'licenceexpdate', 'expdate'])).trim();
      const billingModel = String(getVal(['billingmodel', 'billing_model', 'model'])).toLowerCase() === 'commission' ? 'commission' : 'subscription';
      const subAmount = String(getVal(['subscriptionamount', 'subscription_amount', 'subamount'])).trim();
      const subType = String(getVal(['subscriptiontype', 'subscription_type'])).toLowerCase() === 'yearly' ? 'yearly' : 'monthly';
      const commRate = String(getVal(['commissionrate', 'commission_rate'])).trim();
      const commType = String(getVal(['commissiontype', 'commission_type'])).toLowerCase() === 'fixed' ? 'fixed' : 'percentage';

      return {
        id: id ? parseInt(id, 10) : null,
        vendorName: name,
        vendorEmail: email,
        vendorConNumber: phone,
        location,
        address,
        regNumber,
        LicenceExpDate: expDate,
        vendor_latlong: combinedLatLong,
        password,
        billing_model: billingModel,
        subscription_amount: subAmount,
        subscription_type: subType,
        commission_rate: commRate,
        commission_type: commType,
      };
    } else {
      const titleType = String(getVal(['title', 'titletype'])).trim() || 'Dr.';
      const specialtyRaw = String(getVal(['specialty', 'speciality', 'specialization', 'specialtyid'])).trim();
      const education = String(getVal(['education', 'qualification', 'degree'])).trim();
      const experience = String(getVal(['experience', 'exp', 'yearsofexperience'])).trim();
      const gender = String(getVal(['gender', 'sex'])).trim();
      const clinicRaw = String(getVal(['clinic', 'clinicname', 'clinicid', 'clinics'])).trim();
      const languages = String(getVal(['languages', 'languagespoken', 'language'])).trim();
      const commitmentRaw = String(getVal(['commitmenttype', 'commitment', 'iscommitted'])).toLowerCase();
      const isCommitted = commitmentRaw.includes('commit') || commitmentRaw === '1';

      return {
        id: id ? parseInt(id, 10) : null,
        titleType,
        vendorName: name,
        vendorEmail: email,
        vendorConNumber: phone,
        specialtyRaw,
        education,
        experience,
        gender,
        regNumber,
        location,
        address,
        vendor_latlong: combinedLatLong,
        clinicRaw,
        languages,
        is_committed: isCommitted ? 1 : 0,
        commitment_type: isCommitted ? 'committed' : 'normal',
        password,
      };
    }
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setResults(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        const rawJson = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (!rawJson || rawJson.length === 0) {
          alert('Uploaded sheet contains no data rows.');
          return;
        }

        // Process each row and match with existing list
        const processed = rawJson.map((row, index) => {
          const item = parseRowFields(row);

          // Find match in existing list
          let matchedExisting = null;
          if (item.id) {
            matchedExisting = existingList.find((ex) => String(ex.id) === String(item.id));
          }
          if (!matchedExisting && item.vendorEmail) {
            matchedExisting = existingList.find(
              (ex) => (ex.vendor_email || ex.vendorEmail || '').toLowerCase() === item.vendorEmail.toLowerCase()
            );
          }
          if (!matchedExisting && item.vendorConNumber) {
            matchedExisting = existingList.find(
              (ex) => String(ex.vendor_phone || ex.vendorConNumber || '').replace(/\D/g, '') === item.vendorConNumber
            );
          }
          if (!matchedExisting && item.regNumber && isClinic) {
            matchedExisting = existingList.find(
              (ex) => (ex.reg_no || ex.regNumber || '').toLowerCase() === item.regNumber.toLowerCase()
            );
          }

          // Validation
          const missingFields = [];
          if (!item.vendorName) missingFields.push('Name');
          if (!item.vendorEmail) missingFields.push('Email');
          if (!item.vendorConNumber || item.vendorConNumber.length < 10) missingFields.push('Valid 10-digit Mobile');

          if (isClinic) {
            if (!item.location) missingFields.push('Location');
            if (!item.address) missingFields.push('Address');
            if (!item.regNumber) missingFields.push('Registration Number');
            if (!item.LicenceExpDate && !matchedExisting) missingFields.push('License Expiry Date');
          } else {
            if (!item.specialtyRaw) missingFields.push('Specialty');
            if (!item.education) missingFields.push('Education');
            if (!item.clinicRaw && !matchedExisting) missingFields.push('Clinic');
          }

          const isUpdate = Boolean(matchedExisting);
          const hasError = missingFields.length > 0;

          // Resolve Doctor Specialty & Clinic if doctor
          let resolvedSpecialtyId = null;
          let resolvedClinicId = null;

          if (!isClinic) {
            // Specialty
            if (item.specialtyRaw) {
              const specMatch = specialities.find(
                (s) =>
                  String(s.id) === item.specialtyRaw ||
                  s.speciality?.toLowerCase() === item.specialtyRaw.toLowerCase()
              );
              resolvedSpecialtyId = specMatch ? specMatch.id : parseInt(item.specialtyRaw, 10) || null;
            }
            if (!resolvedSpecialtyId && matchedExisting) {
              resolvedSpecialtyId = matchedExisting.specialty_id || matchedExisting.doctor_speciality_id;
            }

            // Clinic
            if (item.clinicRaw) {
              const clinicMatch = clinics.find(
                (c) =>
                  String(c.id) === item.clinicRaw ||
                  (c.vendor_name || c.vendorName || '').toLowerCase() === item.clinicRaw.toLowerCase()
              );
              resolvedClinicId = clinicMatch ? clinicMatch.id : parseInt(item.clinicRaw, 10) || null;
            }
            if (!resolvedClinicId && matchedExisting) {
              resolvedClinicId = matchedExisting.clinic_id || matchedExisting.clinic_vendor_id;
            }
          }

          return {
            rowIndex: index + 1,
            data: item,
            matchedExisting,
            targetId: matchedExisting ? matchedExisting.id : null,
            action: hasError ? 'ERROR' : isUpdate ? 'UPDATE' : 'CREATE',
            errors: missingFields,
            resolvedSpecialtyId,
            resolvedClinicId,
          };
        });

        setParsedRows(processed);
      } catch (err) {
        alert(`Error parsing Excel file: ${err.message}`);
      }
    };
    reader.readAsBinaryString(uploadedFile);
  };

  const handleResetFile = () => {
    setFile(null);
    setParsedRows([]);
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─────────────────────────────────────────────────────────────
  // 4. BATCH UPLOAD EXECUTION ENGINE
  // ─────────────────────────────────────────────────────────────
  const handleStartImport = async () => {
    const validRows = parsedRows.filter((r) => r.action === 'CREATE' || r.action === 'UPDATE');
    if (validRows.length === 0) {
      alert('No valid rows to process.');
      return;
    }

    setIsProcessing(true);
    setProgress({ current: 0, total: validRows.length, percent: 0, currentItem: '' });

    const adminId = Cookies.get('adminid') || localStorage.getItem('adminid') || '16';
    let successCreated = 0;
    let successUpdated = 0;
    let failCount = 0;
    const log = [];

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      const d = row.data;
      const itemName = d.vendorName || `Row ${row.rowIndex}`;

      setProgress({
        current: i + 1,
        total: validRows.length,
        percent: Math.round(((i + 1) / validRows.length) * 100),
        currentItem: `${row.action === 'UPDATE' ? 'Updating' : 'Creating'}: ${itemName}`,
      });

      try {
        if (isClinic) {
          const clinicPayload = {
            vendorTypeId: 3,
            vendorName: d.vendorName,
            vendorConNumber: d.vendorConNumber,
            vendorEmail: d.vendorEmail,
            regNumber: d.regNumber,
            taxnumber: '',
            LicenceExpDate: d.LicenceExpDate ? String(d.LicenceExpDate).slice(0, 10) : '2027-12-31',
            address: d.address,
            location: d.location,
            doctorList: '[]',
            insuranceList: '',
            salesManRefCode: '',
            insurance_Yes_No: '',
            vendor_latlong: d.vendor_latlong || '',
            pharmacydataArray: '[]',
            adminId: adminId,
            password: d.password || 'Clinic@123',
          };

          const files = {
            profilePhoto: undefined,
            license: undefined,
            authorizedSignature: undefined,
            commercialLicense: undefined,
          };

          let savedClinicId = null;
          if (row.action === 'UPDATE') {
            const clinicId = row.targetId;
            const res = await ClinicService.updateClinic(clinicId, clinicPayload, files);
            if (res && (res.status === 1 || res.status === '1' || res.data)) {
              successUpdated++;
              savedClinicId = clinicId;
              log.push({ name: itemName, action: 'UPDATE', status: 'SUCCESS', msg: 'Clinic updated successfully' });
            } else {
              failCount++;
              log.push({ name: itemName, action: 'UPDATE', status: 'FAILED', msg: res?.msg || 'Update returned error' });
            }
          } else {
            const res = await ClinicService.addClinic(clinicPayload, files);
            if (res && (res.status === 1 || res.status === '1' || res.clinic_id || res.data?.id)) {
              successCreated++;
              savedClinicId = res.clinic_id || res.data?.id;
              log.push({ name: itemName, action: 'CREATE', status: 'SUCCESS', msg: 'Clinic created successfully' });
            } else {
              failCount++;
              log.push({ name: itemName, action: 'CREATE', status: 'FAILED', msg: res?.msg || 'Create returned error' });
            }
          }

          // Save Billing details if provided
          if (savedClinicId && (d.billing_model || d.subscription_amount || d.commission_rate)) {
            try {
              await apiFetch(`${API_BASE_URL}/clinic-billing/${savedClinicId}`, {
                method: 'PUT',
                body: JSON.stringify({
                  billing_model: d.billing_model,
                  subscription_amount: d.subscription_amount || '0',
                  subscription_type: d.subscription_type || 'monthly',
                  commission_rate: d.commission_rate || '0',
                  commission_type: d.commission_type || 'percentage',
                }),
              });
            } catch (_) {}
          }
        } else {
          // DOCTOR IMPORT / UPDATE
          const primaryClinicId = row.resolvedClinicId || clinics[0]?.id || 16;
          const specialtyId = row.resolvedSpecialtyId || specialities[0]?.id || 1;

          const doctorPayload = {
            titleType: d.titleType || 'Dr.',
            vendorName: d.vendorName,
            vendorEmail: d.vendorEmail,
            vendorConNumber: d.vendorConNumber,
            specialty: specialtyId,
            vendor_contact_qualification: d.education,
            experience: d.experience || '',
            vendor_experience: d.experience || '',
            gender: d.gender || '',
            vendor_regNumber: d.regNumber || '',
            vendor_address: d.address || '',
            location: d.location || '',
            vendor_latlong: d.vendor_latlong || '',
            clinic_id: primaryClinicId,
            clinic_ids: [primaryClinicId],
            conditions: [],
            procedures: [],
            is_committed: d.is_committed || 0,
            commitment_type: d.commitment_type || 'normal',
            languages: d.languages || '',
            admin_id: adminId,
            password: d.password || 'Doctor@123',
          };

          const files = {
            profilePhoto: undefined,
            license: undefined,
            authorizedSignature: undefined,
            commercialLicense: undefined,
            civilId: undefined,
          };

          if (row.action === 'UPDATE') {
            const doctorId = row.targetId;
            const res = await DoctorService.updateDoctor(doctorId, doctorPayload, files);
            if (res && (res.status === 1 || res.status === '1' || res.data)) {
              successUpdated++;
              log.push({ name: itemName, action: 'UPDATE', status: 'SUCCESS', msg: 'Doctor updated successfully' });
            } else {
              failCount++;
              log.push({ name: itemName, action: 'UPDATE', status: 'FAILED', msg: res?.msg || 'Update returned error' });
            }
          } else {
            const res = await DoctorService.addDoctor(doctorPayload, files);
            if (res && (res.status === 1 || res.status === '1' || res.doctor_id || res.data?.id)) {
              successCreated++;
              log.push({ name: itemName, action: 'CREATE', status: 'SUCCESS', msg: 'Doctor registered successfully' });
            } else {
              failCount++;
              log.push({ name: itemName, action: 'CREATE', status: 'FAILED', msg: res?.msg || 'Add returned error' });
            }
          }
        }
      } catch (err) {
        failCount++;
        log.push({ name: itemName, action: row.action, status: 'FAILED', msg: err.message });
      }
    }

    setIsProcessing(false);
    setResults({
      created: successCreated,
      updated: successUpdated,
      failed: failCount,
      log,
    });

    if (onSuccess && (successCreated > 0 || successUpdated > 0)) {
      onSuccess();
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 5. COUNTS & FILTERING
  // ─────────────────────────────────────────────────────────────
  const createCount = parsedRows.filter((r) => r.action === 'CREATE').length;
  const updateCount = parsedRows.filter((r) => r.action === 'UPDATE').length;
  const errorCount = parsedRows.filter((r) => r.action === 'ERROR').length;

  const filteredRows = parsedRows.filter((r) => {
    if (filterTab === 'create') return r.action === 'CREATE';
    if (filterTab === 'update') return r.action === 'UPDATE';
    if (filterTab === 'error') return r.action === 'ERROR';
    return true;
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-100 relative overflow-hidden animate-fadeIn">
        {/* MODAL HEADER */}
        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center border border-teal-100 shadow-sm">
              <FileSpreadsheet size={22} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800 leading-tight">
                Bulk Upload & Update {entityTitle}s
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                Upload Excel file to add new {type}s or update existing ones in one click.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* MODAL BODY */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {/* STEP 1: DOWNLOAD TEMPLATE & EXPORT CURRENT LIST */}
          <div className="bg-teal-50/40 border border-teal-100 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <Info size={18} className="text-teal-600 shrink-0" />
              <div className="text-xs text-slate-600">
                <span className="font-bold text-slate-800">Need the Excel template or want to edit current data?</span>
                <br />
                Download our pre-formatted template or export existing records to edit in Excel and re-upload.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadTemplate}
                className="px-3.5 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all"
              >
                <Download size={14} /> Download {entityTitle} Template (.xlsx)
              </button>
              <button
                onClick={handleExportExisting}
                className="px-3.5 py-2 rounded-lg bg-white border border-teal-200 hover:bg-teal-50 text-teal-800 text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all"
              >
                <Download size={14} /> Export Current {entityTitle}s (.xlsx)
              </button>
            </div>
          </div>

          {/* STEP 2: FILE UPLOAD DROPZONE */}
          {!file && (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-200 hover:border-teal-500 rounded-2xl p-8 text-center cursor-pointer transition-all bg-slate-50/50 hover:bg-teal-50/20 group"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div className="w-12 h-12 rounded-full bg-teal-50 group-hover:bg-teal-100 text-teal-600 mx-auto flex items-center justify-center mb-3 transition-colors">
                <FileUp size={24} />
              </div>
              <p className="text-sm font-bold text-slate-700 mb-1">
                Click to browse or drag and drop your Excel file here
              </p>
              <p className="text-xs text-slate-400">Supports .xlsx, .xls, .csv files</p>
            </div>
          )}

          {/* ACTIVE FILE BANNER */}
          {file && (
            <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={20} className="text-teal-600" />
                <div>
                  <p className="text-xs font-bold text-slate-800">{file.name}</p>
                  <p className="text-[11px] text-slate-400">
                    {(file.size / 1024).toFixed(1)} KB • {parsedRows.length} rows loaded
                  </p>
                </div>
              </div>
              {!isProcessing && (
                <button
                  onClick={handleResetFile}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700 hover:underline"
                >
                  Change File
                </button>
              )}
            </div>
          )}

          {/* STEP 3: PREVIEW TABLE & ACTION METRICS */}
          {parsedRows.length > 0 && !results && (
            <div>
              {/* METRIC PILLS & TABS */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-lg">
                  <button
                    onClick={() => setFilterTab('all')}
                    className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                      filterTab === 'all' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    All ({parsedRows.length})
                  </button>
                  <button
                    onClick={() => setFilterTab('create')}
                    className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                      filterTab === 'create'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-emerald-700 hover:bg-emerald-50'
                    }`}
                  >
                    New ({createCount})
                  </button>
                  <button
                    onClick={() => setFilterTab('update')}
                    className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                      filterTab === 'update' ? 'bg-blue-600 text-white shadow-sm' : 'text-blue-700 hover:bg-blue-50'
                    }`}
                  >
                    Update Existing ({updateCount})
                  </button>
                  {errorCount > 0 && (
                    <button
                      onClick={() => setFilterTab('error')}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                        filterTab === 'error' ? 'bg-rose-600 text-white shadow-sm' : 'text-rose-700 hover:bg-rose-50'
                      }`}
                    >
                      Errors ({errorCount})
                    </button>
                  )}
                </div>
                <div className="text-xs text-slate-500 font-medium">
                  {updateCount} will be updated in system • {createCount} will be newly registered
                </div>
              </div>

              {/* TABLE CONTAINER */}
              <div className="border border-slate-200 rounded-xl overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-slate-50 text-slate-600 font-bold sticky top-0 border-b border-slate-200 z-10">
                    <tr>
                      <th className="py-2.5 px-3">#</th>
                      <th className="py-2.5 px-3">Action</th>
                      <th className="py-2.5 px-3">Name</th>
                      <th className="py-2.5 px-3">Email & Phone</th>
                      <th className="py-2.5 px-3">Location / Coords</th>
                      {isClinic ? (
                        <th className="py-2.5 px-3">Reg & Expiry</th>
                      ) : (
                        <th className="py-2.5 px-3">Specialty & Clinic</th>
                      )}
                      <th className="py-2.5 px-3">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredRows.map((row) => {
                      const d = row.data;
                      const isCreate = row.action === 'CREATE';
                      const isUpdate = row.action === 'UPDATE';
                      const isError = row.action === 'ERROR';

                      return (
                        <tr
                          key={row.rowIndex}
                          className={`hover:bg-slate-50/70 transition-colors ${
                            isError ? 'bg-rose-50/30' : isUpdate ? 'bg-blue-50/20' : ''
                          }`}
                        >
                          <td className="py-2 px-3 text-slate-400 font-mono">{row.rowIndex}</td>
                          <td className="py-2 px-3">
                            {isCreate && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                CREATE
                              </span>
                            )}
                            {isUpdate && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                UPDATE
                              </span>
                            )}
                            {isError && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200">
                                ERROR
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 font-semibold text-slate-800">
                            {!isClinic && d.titleType ? `${d.titleType} ` : ''}
                            {d.vendorName || <span className="text-rose-400 font-normal">Missing Name</span>}
                          </td>
                          <td className="py-2 px-3">
                            <div className="font-medium text-slate-700">{d.vendorEmail || '—'}</div>
                            <div className="text-[11px] text-slate-400">{d.vendorConNumber || '—'}</div>
                          </td>
                          <td className="py-2 px-3">
                            <div className="text-slate-700">{d.location || '—'}</div>
                            {isClinic && (
                              d.vendor_latlong ? (
                                <span className="text-[10px] text-teal-600 font-mono">📍 {d.vendor_latlong}</span>
                              ) : (
                                <span className="text-[10px] text-slate-300">No coords</span>
                              )
                            )}
                          </td>
                          {isClinic ? (
                            <td className="py-2 px-3">
                              <div className="text-slate-700">Reg: {d.regNumber || '—'}</div>
                              <div className="text-[11px] text-slate-400">Exp: {d.LicenceExpDate || '—'}</div>
                            </td>
                          ) : (
                            <td className="py-2 px-3">
                              <div className="font-semibold text-slate-700">{d.specialtyRaw || '—'}</div>
                              <div className="text-[11px] text-slate-400">Clinic: {d.clinicRaw || '—'}</div>
                            </td>
                          )}
                          <td className="py-2 px-3 text-[11px]">
                            {isError && (
                              <span className="text-rose-600 font-medium">Missing: {row.errors.join(', ')}</span>
                            )}
                            {isUpdate && (
                              <span className="text-blue-700 font-medium">
                                Updates #{row.matchedExisting.id} ({row.matchedExisting.vendor_name || row.matchedExisting.vendorName})
                              </span>
                            )}
                            {isCreate && <span className="text-emerald-700 font-medium">New registration</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 4: PROCESSING PROGRESS */}
          {isProcessing && (
            <div className="p-5 bg-teal-50/50 border border-teal-200 rounded-xl space-y-3">
              <div className="flex items-center justify-between text-xs font-bold text-teal-900">
                <span className="flex items-center gap-2">
                  <RefreshCw size={14} className="animate-spin text-teal-600" />
                  {progress.currentItem || 'Processing...'}
                </span>
                <span>
                  {progress.current} / {progress.total} ({progress.percent}%)
                </span>
              </div>
              <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                <div
                  className="bg-teal-600 h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Please do not close this window while the system updates and creates {type}s.
              </p>
            </div>
          )}

          {/* STEP 5: FINAL SUMMARY RESULTS */}
          {results && (
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">Bulk Operation Completed!</h3>
                  <p className="text-xs text-slate-500">
                    System records have been updated successfully with your Excel data.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-white border border-emerald-200 rounded-xl text-center">
                  <p className="text-2xl font-extrabold text-emerald-600">{results.created}</p>
                  <p className="text-xs font-bold text-slate-600">Newly Created</p>
                </div>
                <div className="p-3 bg-white border border-blue-200 rounded-xl text-center">
                  <p className="text-2xl font-extrabold text-blue-600">{results.updated}</p>
                  <p className="text-xs font-bold text-slate-600">Updated in System</p>
                </div>
                <div className="p-3 bg-white border border-rose-200 rounded-xl text-center">
                  <p className="text-2xl font-extrabold text-rose-600">{results.failed}</p>
                  <p className="text-xs font-bold text-slate-600">Failed / Errors</p>
                </div>
              </div>

              {results.log && results.log.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg bg-white p-3 divide-y divide-slate-100 text-xs">
                  {results.log.map((item, idx) => (
                    <div key={idx} className="py-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {item.status === 'SUCCESS' ? (
                          <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                        ) : (
                          <AlertCircle size={14} className="text-rose-500 shrink-0" />
                        )}
                        <span className="font-semibold text-slate-800">{item.name}</span>
                        <span className="text-[10px] text-slate-400">({item.action})</span>
                      </div>
                      <span className={item.status === 'SUCCESS' ? 'text-emerald-600' : 'text-rose-600 font-medium'}>
                        {item.msg}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* MODAL FOOTER */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            {results ? 'Close' : 'Cancel'}
          </button>

          {!results && parsedRows.length > 0 && (
            <button
              onClick={handleStartImport}
              disabled={isProcessing || createCount + updateCount === 0}
              className={`px-5 py-2.5 rounded-xl text-xs font-bold text-white flex items-center gap-2 shadow-sm transition-all ${
                createCount + updateCount > 0 && !isProcessing
                  ? 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/20'
                  : 'bg-slate-300 cursor-not-allowed'
              }`}
            >
              {isProcessing ? (
                <>
                  <RefreshCw size={14} className="animate-spin" /> Processing...
                </>
              ) : (
                <>
                  <Upload size={14} /> Process & Apply {createCount + updateCount} Records to System
                </>
              )}
            </button>
          )}

          {results && (
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 transition-all shadow-sm"
            >
              Done & View Updated {entityTitle}s
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
